import React, { forwardRef, Component } from 'react'

/**
 * Lazy-import ReactQuill so it isn't server-side rendered and doesn't fire
 * findDOMNode on a null host during React 18 Strict Mode double-mount.
 */
const ReactQuill = React.lazy(() =>
	Promise.all([
		import('react-quill'),
		import('react-quill/dist/quill.snow.css'),
	]).then(([mod]) => {
		const Q = mod.default || mod
		return { default: Q }
	})
)

/** Minimal fallback so the page never goes blank if Quill crashes */
class QuillErrorBoundary extends Component {
	constructor(props) {
		super(props)
		this.state = { crashed: false }
	}

	static getDerivedStateFromError() {
		return { crashed: true }
	}

	render() {
		if (this.state.crashed) {
			return (
				<textarea
					className="w-full border border-gray-200 rounded-lg p-3 text-sm text-gray-800 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-custom-600/20 focus:border-custom-600"
					value={this.props.value || ''}
					onChange={(e) => this.props.onChange && this.props.onChange(e.target.value)}
					placeholder="Enter text…"
				/>
			)
		}
		return this.props.children
	}
}

const RichTextEditor = forwardRef((props, ref) => {
	return (
		<div className="rich-text-editor">
			<QuillErrorBoundary value={props.value} onChange={props.onChange}>
				<React.Suspense fallback={
					<div className="border border-gray-200 rounded-lg min-h-[120px] flex items-center justify-center bg-gray-50">
						<span className="text-sm text-gray-400">Loading editor…</span>
					</div>
				}>
					<ReactQuill ref={ref} {...props} />
				</React.Suspense>
			</QuillErrorBoundary>
		</div>
	)
})

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor
